import { FunctionComponent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	faCheck,
	faEye,
	faFloppyDisk,
	faPen,
	faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { InputField } from "@/components/InputField/InputField";
import { Modal } from "@/components";

import { CourseVersion } from "@/pages/Private/pages/Courses/schema/courses";
import {
	useCreateCourseVersionMutation,
	useGetCourseQuery,
	useGetCourseVersionsQuery,
	useDeleteCourseVersionMutation,
	usePublishCourseVersionMutation,
	useUpdateCourseVersionMutation,
} from "@/pages/Private/redux/courses/courses.api";
import { isUnreachableError, readApiErrorMessage } from "@/utils/apiError";
import { classNames } from "@/utils/classNames";

import { CourseStatus } from "../../enums/CourseStatus.enum";

interface VersionsTabProps {
	courseId: number;
	/**
	 * Loads a version's content into the editor. Nothing is written: the stored draft is
	 * only replaced if the author then saves, which is what makes this safe to press.
	 */
	onView?: (version: CourseVersion) => void;
}

/*
 * TODO(F19, environment): thumbnail and trailer assets 503 from
 * crisis-dev.nbg1.your-objectstorage.com in dev, so both fields render empty and cannot
 * be judged at all. Nothing to fix in this file — noted here because it blocks reviewing
 * the media fields, and whoever picks up the list above will hit it.
 */
const formatMoment = (value?: string | null): string =>
	value ? new Date(value).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }) : "";

/**
 * The scenario's saved versions — BCM360-139.
 *
 * A version is a snapshot; publishing one copies it onto the scenario, which is what every
 * player reads. So "has been published" and "is playing now" are two different questions:
 * `publishedAt` answers the first and is stamped every time a version went live, while the
 * scenario's own `publishedVersionId` answers the second. A version can carry a publish
 * date and no longer be the active one.
 */
export const VersionsTab: FunctionComponent<VersionsTabProps> = ({ courseId, onView }) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`editor.versions.${key}`, options ?? {}),
		[t]
	);

	const { data: course, refetch: refetchCourse } = useGetCourseQuery(courseId);
	const { data: versions, isLoading } = useGetCourseVersionsQuery(courseId);

	const [createVersion, { isLoading: isCreating }] = useCreateCourseVersionMutation();
	const [updateVersion, { isLoading: isRenaming }] = useUpdateCourseVersionMutation();
	const [publishVersion, { isLoading: isPublishing }] = usePublishCourseVersionMutation();
	const [deleteVersion, { isLoading: isDeleting }] = useDeleteCourseVersionMutation();

	const [newLabel, setNewLabel] = useState("");
	const [editing, setEditing] = useState<CourseVersion | null>(null);
	const [editingLabel, setEditingLabel] = useState("");
	const [publishing, setPublishing] = useState<CourseVersion | null>(null);
	const [deleting, setDeleting] = useState<CourseVersion | null>(null);
	const [error, setError] = useState("");

	const isBusy = isCreating || isRenaming || isPublishing || isDeleting;

	/**
	 * The server's own words where it gave any — the API says which entries it refuses to
	 * delete and why — and an honest "could not reach it" where the request never landed,
	 * since telling someone to try again is wrong advice for a server that is not there.
	 */
	const describe = (caught: unknown, fallback: string) =>
		isUnreachableError(caught) ? ts("unreachable") : readApiErrorMessage(caught) || ts(fallback);

	/**
	 * The working copy sits on top, whatever its date: it is the only entry that is not
	 * history, and the one an author is looking for when they open this. Saves rewrite it
	 * in place rather than appending, so the rest below it grows only on publishes.
	 */
	const all = versions ?? [];
	const draft = all.find((version) => version.isDraft) ?? null;
	const history = all.filter((version) => !version.isDraft);

	const handleSave = async () => {
		setError("");

		try {
			await createVersion({ id: courseId, label: newLabel.trim() || undefined }).unwrap();
			setNewLabel("");
		} catch (caught) {
			setError(describe(caught, "saveFailed"));
		}
	};

	const handleRename = async () => {
		if (!editing) {
			return;
		}

		setError("");

		try {
			await updateVersion({
				id: courseId,
				versionId: editing.id,
				label: editingLabel.trim(),
			}).unwrap();
			setEditing(null);
		} catch (caught) {
			setError(describe(caught, "renameFailed"));
		}
	};

	const handleDelete = async () => {
		if (!deleting) {
			return;
		}

		setError("");

		try {
			await deleteVersion({ id: courseId, versionId: deleting.id }).unwrap();
			setDeleting(null);
		} catch (caught) {
			setDeleting(null);
			// The API says which entries it will not delete and why; that is more use than
			// a generic refusal.
			setError(describe(caught, "deleteFailed"));
		}
	};

	const handlePublish = async () => {
		if (!publishing) {
			return;
		}

		setError("");

		try {
			await publishVersion({ id: courseId, versionId: publishing.id }).unwrap();
			// Publishing rewrites the scenario's own content and its publishedVersionId, and
			// the server may add a snapshot of what was live before — so the scenario is
			// re-read rather than patched from what was sent.
			await refetchCourse();
			setPublishing(null);
		} catch (caught) {
			setPublishing(null);
			setError(describe(caught, "publishFailed"));
		}
	};

	return (
		<div className="space-y-4">
			<div className="space-y-2 rounded-card border border-border-soft bg-surface p-4">
				<span className="block text-sm font-bold text-ink">{ts("saveHeading")}</span>
				<p className="text-xs text-subtle">{ts("saveHint")}</p>
				{/*
				 * One row, one height. The field defaults to py-3 and a bottom margin, which
				 * made it half again as tall as the button beside it and left them looking like
				 * two unrelated controls that happened to be adjacent.
				 */}
				<div className="flex flex-wrap items-center gap-2">
					<InputField
						className="!h-[32px] !py-0 text-xs"
						containerClassName="min-w-[220px] flex-1"
						handleChange={(event) => setNewLabel(event.target.value)}
						name="version-label"
						placeholder={ts("labelPlaceholder")}
						value={newLabel}
					/>
					<Button
						className="!w-auto"
						color={ButtonColor.ACTION}
						disabled={isBusy}
						image={<Icon className="mr-2" icon={faFloppyDisk} />}
						size={ButtonSize.S}
						title={ts("save")}
						type="button"
						onClick={handleSave}
					/>
				</div>
			</div>

			{!!error && (
				<p className="rounded-card bg-error-light px-3 py-2 text-xs font-semibold text-text">
					{error}
				</p>
			)}

			{!!draft && (
				<div className="space-y-2 rounded-card border border-warning-ink bg-warning-light p-3">
					<div className="flex flex-wrap items-center gap-2">
						<span className="min-w-0 flex-1 truncate text-sm font-bold text-ink">
							{ts("draftHeading")}
						</span>
						<span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-bold text-warning-ink">
							{ts("draftBadge")}
						</span>
					</div>

					<p className="text-xs text-warning-ink">{ts("draftHint")}</p>

					{/*
					 * No actions here. A draft has never been live, and publishing it is the
					 * editor's own Publish button rather than a second place doing the same thing.
					 */}
				</div>
			)}

			{isLoading ? (
				<p className="text-xs text-subtle">{ts("loading")}</p>
			) : !history.length ? (
				<p className="rounded-card border border-dashed border-border bg-surface px-4 py-6 text-center text-sm text-subtle">
					{/*
					 * TODO(F12): this reads "nothing published yet" while the bar below shows
					 * Live with an Unpublish button — a scenario made live before versioning
					 * existed has no rows to show but is emphatically published. Ask the course's
					 * status here rather than inferring it from an empty list. Two more in the
					 * same family: the yellow Working copy card comes and goes out of step with
					 * the save bar's own unsaved state, and draftHint says "use Publish above"
					 * when Publish is in the bar below.
					 */}
					{ts("empty")}
				</p>
			) : (
				<ul className="space-y-2">
					{history.map((version) => {
						const isActive = course?.publishedVersionId === version.id;
						// Unpublishing leaves the pointer where it was, so the scenario can name what
						// it last showed while showing nothing — "active" would be a lie meanwhile.
						const isLive = isActive && course?.status === CourseStatus.Active;

						return (
							<li
								key={version.id}
								className={classNames(
									"space-y-2 rounded-card border p-3",
									// The border alone marks the live one. A tinted row reads as selected —
									// something the author just clicked — when what it means is that this is
									// the version everybody is playing, which nothing here selected.
									isActive ? "border-accent bg-white" : "border-border-soft bg-white"
								)}
							>
								<div className="flex flex-wrap items-center gap-2">
									<span className="min-w-0 flex-1 truncate text-sm font-bold text-ink">
										{/*
										 * TODO(F14): two naming schemes in one list. A version saved with
										 * an empty name falls back to this — the database row id, which is
										 * global, so it reads "Version 31" — while publishing names its
										 * own "Version 2" from a per-scenario count. Pick the per-scenario
										 * number for both, which means the server naming a manual save
										 * too. Also: the save field accepts an empty name with no
										 * validation and gives no confirmation that anything happened.
										 */}
										{version.label || ts("untitled", { id: version.id })}
									</span>

									{/* Two different facts, so two different badges: one says this was
									    live at some point, the other says it is what plays right now. */}
									{isActive && (
										<span
											className={classNames(
												"shrink-0 rounded-full px-2 py-0.5 text-xs font-bold",
												isLive ? "bg-success-light text-text" : "bg-surface text-subtle"
											)}
										>
											{isLive ? <Icon icon={faCheck} /> : null}{" "}
											{isLive ? ts("active") : ts("lastPublished")}
										</span>
									)}
									{!!version.publishedAt && (
										<span className="shrink-0 rounded-full bg-surface px-2 py-0.5 text-xs font-semibold text-subtle">
											{ts("publishedAt", { date: formatMoment(version.publishedAt) })}
										</span>
									)}
								</div>

								<p className="text-xs text-subtle">
									{ts("createdAt", { date: formatMoment(version.createdAt) })}
									{version.createdByName
										? ` · ${ts("createdBy", { name: version.createdByName })}`
										: ""}
								</p>

								<div className="flex flex-wrap gap-2">
									<Button
										className="!w-auto"
										color={ButtonColor.ACTION_SECONDARY}
										disabled={isBusy}
										image={<Icon className="mr-2" icon={faPen} />}
										size={ButtonSize.XS}
										title={ts("rename")}
										type="button"
										// TODO(F15): opens blank for a version created earlier in the same
										// session, and pre-fills correctly after a reload — so the row this
										// reads from is the one the create response put in the cache, which
										// evidently lacks the label the list later shows. Compare what the
										// POST returns with what the list returns.
										onClick={() => {
											setEditing(version);
											setEditingLabel(version.label ?? "");
										}}
									/>
									{(!isActive || !isLive) && (
										<Button
											className="!w-auto"
											color={ButtonColor.ACTION}
											disabled={isBusy}
											size={ButtonSize.XS}
											title={ts("publish")}
											type="button"
											onClick={() => setPublishing(version)}
										/>
									)}
									{/*
									 * F11 was the second suspect: the load always worked, but the flow is
									 * kept mounted behind whichever tab is open, so it landed on a screen
									 * nobody was looking at. `EditorRightPanel` shows the flow now.
									 *
									 * TODO(F16): publishing an older version also rolls the working copy
									 * back to that version's content, discarding unsaved edits. The
									 * confirm promises only that "the current state is kept as a version
									 * of its own" and says nothing about the draft. Either keep the draft
									 * or say plainly that it is discarded.
									 */}
									{/* Loads it into the editor and no further: the draft on the server is
									    untouched until the author saves, so looking costs nothing. */}
									{!!onView && (
										<Button
											className="!w-auto"
											color={ButtonColor.ACTION_SECONDARY}
											disabled={isBusy}
											image={<Icon className="mr-2" icon={faEye} />}
											size={ButtonSize.XS}
											title={ts("view")}
											type="button"
											onClick={() => onView(version)}
										/>
									)}
									{/* The API refuses to delete whatever the scenario is published from, so
									    that entry is not offered the action rather than shown one that fails. */}
									{!isActive && (
										<Button
											className="!w-auto"
											color={ButtonColor.DANGER}
											disabled={isBusy}
											image={<Icon className="mr-2" icon={faTrashCan} />}
											size={ButtonSize.XS}
											title={ts("delete")}
											type="button"
											onClick={() => setDeleting(version)}
										/>
									)}
								</div>
							</li>
						);
					})}
				</ul>
			)}

			<Modal
				handleClose={() => setEditing(null)}
				handleSave={handleRename}
				isOpened={!!editing}
				size="xs"
				submitButtonText={ts("renameSave")}
				title={ts("renameTitle")}
			>
				<InputField
					handleChange={(event) => setEditingLabel(event.target.value)}
					name="version-rename"
					placeholder={ts("labelPlaceholder")}
					value={editingLabel}
				/>
			</Modal>

			<Modal
				handleClose={() => setDeleting(null)}
				handleSave={handleDelete}
				isOpened={!!deleting}
				size="xs"
				submitButtonColor="error"
				submitButtonText={ts("deleteConfirm")}
				title={ts("deleteTitle")}
			>
				<p className="text-sm text-text">
					{ts("deleteWarning", {
						label: deleting?.label || ts("untitled", { id: deleting?.id ?? 0 }),
					})}
				</p>
			</Modal>

			{/* Publishing is the one action here that changes what other people see, so it is
			    the one that asks first — and says who is affected rather than "are you sure". */}
			<Modal
				handleClose={() => setPublishing(null)}
				handleSave={handlePublish}
				isOpened={!!publishing}
				size="xs"
				submitButtonText={ts("publishConfirm")}
				title={ts("publishTitle")}
			>
				<p className="text-sm text-text">
					{ts("publishWarning", {
						label: publishing?.label || ts("untitled", { id: publishing?.id ?? 0 }),
					})}
				</p>
			</Modal>
		</div>
	);
};
